import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Settings as GearIcon } from "lucide-react";
import { trpc } from "@/trpc/client";
import { Button, Card, Modal, PageHeading } from "@/ui";
import { muted } from "@/components/shared/styles";
import { TemplatesTable } from "#/components/templates/TemplatesTable";
import { TemplateThemeColors } from "@/components/templates/TemplateThemeColors";
import { ConsentTemplatePreview } from "@/components/templates/ConsentTemplatePreview";
import {
  DEFAULT_TEMPLATE_THEME,
  type TemplateTheme,
} from "@/lib/templateTheme";

export const Route = createFileRoute(
  "/dashboard/w/$workspaceId/sending/templates",
)({
  component: TemplatesPage,
});

/** The edit affordance beside the page heading — opens the section's modal.
 * Lifted from the retired `SettingsForm.tsx` along with the colour editor
 * below; that file is gone now and this is the pattern's sole caller, so it
 * stays local rather than becoming a shared module for one. */
function GearButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex items-center justify-center rounded p-1"
      style={{ color: "var(--gs-muted)" }}
    >
      <GearIcon className="size-4" aria-hidden />
    </button>
  );
}

// The transactional catalogue carries no title of its own: the
// `<h1>` above it already names it, and a "Templates" card
// under an "Email templates" heading reads as two names for one list. The
// heading action keeps the colour picker available without adding a second
// settings section below the catalogue.
function TemplatesPage() {
  const { workspaceId } = Route.useParams();

  const settingsQuery = useQuery(
    trpc.authenticatedRouterFunction.getWorkspaceSettings.queryOptions({
      workspaceId,
    }),
  );

  const templateTheme = settingsQuery.data?.templateTheme ?? DEFAULT_TEMPLATE_THEME;

  // Colour-editor state, carried over from `SettingsForm.tsx`: a draft the
  // modal edits, seeded from the saved theme once per workspace so a refetch
  // this page's own query triggers (e.g. after the colours save) can't clobber
  // an in-progress, unsaved edit.
  //
  // Keyed by workspace, not a boolean — the one thing that did NOT carry over
  // verbatim (Copilot on #755). `$workspaceId` changes without remounting this
  // route, so a ref that only ever flips once would leave the PREVIOUS
  // workspace's colours in the draft, shown as if they were this one's; and
  // because `TemplateThemeColors` reads dirty as "draft differs from the
  // loaded row", its Save would be live and would write them onto the
  // workspace the publisher just moved to. Same guard, same reason, as
  // `SenderIdentityForm`'s `seededRef`.
  //
  // The draft is dropped the moment the workspace changes, not when the new
  // row arrives, so it never holds the previous workspace's colours even for
  // the length of the fetch. Today that window is also covered downstream —
  // `TemplateThemeColors` shows a spinner while loading and reads an unloaded
  // row as clean — but this keeps the draft right without leaning on that.
  // The ref only records a workspace once its row has seeded the draft, so
  // the row's arrival still seeds it and a later refetch leaves it alone.
  const savedTemplateTheme = settingsQuery.data?.templateTheme;
  const [draftTemplateTheme, setDraftTemplateTheme] = useState<TemplateTheme>(
    DEFAULT_TEMPLATE_THEME,
  );
  const templateThemeSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (templateThemeSeededRef.current === workspaceId) return;
    templateThemeSeededRef.current = savedTemplateTheme ? workspaceId : null;
    setDraftTemplateTheme(savedTemplateTheme ?? DEFAULT_TEMPLATE_THEME);
  }, [savedTemplateTheme, workspaceId]);

  const [templateColorsModalOpen, setTemplateColorsModalOpen] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <PageHeading
        title="Email templates"
        action={
          <GearButton
            label="Edit email template colors"
            onClick={() => setTemplateColorsModalOpen(true)}
          />
        }
      />
      <Card>
        <div className="flex flex-col gap-5">
          <p className="text-xs" style={muted}>
            Templates are transactional: they reach any address you name, whether or not it is on your
            subscriber list, and they carry no subscribe or unsubscribe footer. Their bodies contain no
            clickable links — a common phishing vector for brand-impersonation scams. Deliver codes and
            values in the message body (for example a one-time passcode via{" "}
            <code style={{ fontFamily: "var(--gs-font-mono)" }}>otp_code</code>) for the user to type
            back into your app, rather than a link to click.
          </p>
          <TemplatesTable templateTheme={templateTheme} />
        </div>
      </Card>

      <ConsentTemplatePreview
        workspaceId={workspaceId}
        savedThemeRevision={`${templateTheme.header}:${templateTheme.accent}`}
      />

      {/*
        No footer Save, and no <form> of its own: `TemplateThemeColors` owns
        its own `updateTemplateTheme` mutation behind its own Save, so a
        dialog-level Save would submit nothing (there is no shared form to
        submit) or, worse, invite a future edit to wire one up that reports
        success over colours it never sent. "Close" rather than "Cancel":
        nothing here is staged for a later Save, so there is nothing to
        cancel — the colours are saved, or they are not.
      */}
      <Modal
        open={templateColorsModalOpen}
        onClose={() => setTemplateColorsModalOpen(false)}
        title="Email template colors"
        footer={
          <Button type="button" variant="ghost" onClick={() => setTemplateColorsModalOpen(false)}>
            Close
          </Button>
        }
      >
        <div className="flex flex-col gap-1.5">
          <p className="text-xs" style={muted}>
            Applied to every transactional template and consent email example on this page.
          </p>
          <TemplateThemeColors
            workspaceId={workspaceId}
            theme={draftTemplateTheme}
            onThemeChange={setDraftTemplateTheme}
            savedTheme={savedTemplateTheme}
            isLoading={settingsQuery.isLoading}
            isError={settingsQuery.isError}
          />
        </div>
      </Modal>
    </div>
  );
}
